import React, { useRef, useCallback, useMemo, useEffect, useReducer, useId, useState } from 'react';
import { flushSync } from 'react-dom';
import type {
  ChatWidgetProps,
  ChatWidgetAPI,
  Attachment,
  OutcomeTrackingConfig,
  Message,
  RecordData,
  ToolCall,
} from './types';
import { RECORD_VIEW_CONFIG } from './constants';
import { STORAGE_KEY, scopedStorageKey } from './constants/storage';
import { validateFiles } from './utils/fileValidation';
import { isSafeUrl } from './utils/urlSafety';
import { getVisibleViews } from './utils/recordNavigation';
import { isReorderLikeAction } from './utils/actionFilters';
import { sanitizeMessagesForStorage } from './utils/messageStorage';
import { useSessionHistory } from './hooks/useSessionHistory';
import { useAutoOpen } from './hooks/useAutoOpen';
import { useAnalytics } from './hooks/useAnalytics';
import { useOpenState } from './hooks/useOpenState';
import { useThread } from './hooks/useThread';
import { useOnlineStatus, useRetryOnReconnect } from './hooks/useOnlineStatus';
import { useChat } from './hooks/useChat';
import { useTelemetry } from './hooks/useTelemetry';
import { useDragDrop } from './hooks/useDragDrop';
import { useLocalStorage } from './hooks/useLocalStorage';
import { ChatConfigProvider } from './context/ChatContext';
import type { ChatConfigValue } from './context/ChatContext';
import ErrorBoundary from './components/ErrorBoundary';
import MinimizedButton from './components/MinimizedButton';
import Header from './components/Header/Header';
import MainContent from './components/MainContent';
import InputArea from './components/InputArea/InputArea';
import PanelWarmup from './components/PanelWarmup';
const PreChatForm = React.lazy(
  () => import(/* webpackChunkName: "prechat" */ './components/PreChatForm')
);
const SessionPanel = React.lazy(
  () => import(/* webpackChunkName: "sessions" */ './components/SessionPanel')
);
const TelemetryPanel = React.lazy(
  () => import(/* webpackChunkName: "telemetry" */ './components/TelemetryPanel')
);
const CustomerEmailCapture = React.lazy(
  () => import(/* webpackChunkName: "email-capture" */ './components/CustomerEmailCapture')
);
import { useResolvedProps } from './hooks/useResolvedProps';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useBusinessActions } from './hooks/useBusinessActions';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useHostIsolation } from './hooks/useHostIsolation';
import { useIntroMessage } from './hooks/useIntroMessage';
import { useRemoteSettings } from './hooks/useRemoteSettings';
import { useOutcomeTracking } from './hooks/useOutcomeTracking';
import { useCommerceActions } from './hooks/useCommerceActions';
import { useSendGatingState, useDomainGate, useSendGating } from './hooks/useSendGating';
import { useConversationLifecycle } from './hooks/useConversationLifecycle';
import { useConversationController } from './hooks/useConversationController';
import { useImperativeApi } from './hooks/useImperativeApi';
import { useTranscriptPrivacy } from './hooks/useTranscriptPrivacy';
import { useWidgetLayout } from './hooks/useWidgetLayout';
import { usePanelWarmup } from './hooks/usePanelWarmup';
import { useLocalMessages } from './hooks/useLocalMessages';
import { useUnreadMessages } from './hooks/useUnreadMessages';
import { useMessageFeedback } from './hooks/useMessageFeedback';
import { uiReducer, initialUIState } from './reducers/uiReducer';
import {
  resolveWidgetStrings,
  resolveTextDirection,
  formatWidgetString,
  formatCount,
} from './utils/i18n';
import { useWidgetStrings } from './i18n/useWidgetStrings';
import { MerchantPresetProvider } from './merchants/MerchantPresetContext';
import { mergeMerchantStrings } from './merchants/strings';

const SETTINGS_CONSOLE_URL_TEMPLATES = /\{customerId\}|\{id\}|:customerId|:id/;
const DEFAULT_SETTINGS_CONSOLE_URL_TEMPLATE = '/customers/{customerId}/chat-widget-settings';
const EMPTY_RECORDS: Required<RecordData> = Object.freeze({
  orders: [],
  products: [],
  subscriptions: [],
  returns: [],
  exchanges: [],
});
// Stable empty props for the idle warm-up render of the panel (see PanelWarmup).
const EMPTY_MESSAGES: Message[] = [];
const EMPTY_TOOL_CALLS: ToolCall[] = [];
const EMPTY_ATTACHMENTS: Attachment[] = [];
const EMPTY_STRINGS: string[] = [];

// ---- ChatWidgetInner ----
const ChatWidgetInner = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidgetInner(props, ref) {
    const { remoteSettings, isMobile } = useRemoteSettings({
      settingsEndpoint: props.settingsEndpoint || null,
      settingsApiKey: props.settingsApiKey ?? props.apiKey ?? null,
      onError: props.onError,
    });

    const resolved = useResolvedProps(props, remoteSettings);
    const {
      readSetting,
      merchantPreset,
      companyName,
      companyLogo,
      agentName,
      agentAvatar,
      theme,
      darkMode,
      welcomeMessage,
      suggestedActions,
      localizeActions,
      placeholder,
      language,
      textDirection,
      translations,
      customerId,
      settingsConsoleUrl: settingsConsoleUrlTemplate,
      settingsConsoleLabel,
      agentOrgId,
      agentId,
      kbCollection,
      kbApiKey,
      kbTopK,
      kbScoreThreshold,
      conversionPrompt,
      sessionHistoryEnabled,
      maxSessions,
      allowAttachments,
      allowVoice,
      allowVideo,
      apiKey,
      widgetAuthToken,
      apiBaseUrl,
      messageEndpoint,
      messageCreateEndpoint,
      streamEndpoint,
      threadEndpoint,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      cartEndpoint,
      cartAttributionEndpoint,
      feedbackEndpoint,
      cartMode,
      analyticsBaseUrl,
      analyticsEndpoint,
      analyticsSampleRate,
      analyticsEnabled,
      analyticsApiKey,
      telemetryEnabled,
      telemetryPollIntervalMs,
      telemetryShowContext,
      telemetryEndpoint,
      provider,
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      defaultOpen,
      subjectProp,
      channel,
      threadIdProp,
      threadIdControlled,
      threadPollIntervalMs,
      department,
      customFields,
      records,
      effectivePersistThread,
      position,
      zIndex,
      showViewTabs: configuredShowViewTabs,
      recordNavigation,
      showTimestamps,
      maxRenderedMessages,
      filteredBubbleOptions,
      maxInputLength,
      inputWarningThreshold,
      maxMessagesPerSession,
      rateLimit,
      maxFileSizeMB,
      maxFileCount,
      allowedFileTypes,
      allowedDomains,
      allowedOrigins,
      availability,
      offlineMessage,
      sessionConfig: configuredSessionConfig,
      autoOpenRules,
      autoOpenDelayMs,
      preChatForm,
      consent,
      doNotTrain,
      retentionDays,
      sessionVariables,
      retryPolicy,
      lazyLoad,
      customCss,
      cspNonce,
      idleTimeout: idleTimeoutConfig,
    } = resolved;
    const sessionConfig = useMemo(
      () =>
        retentionDays === 0
          ? { ...configuredSessionConfig, persistThreadTokens: false }
          : configuredSessionConfig,
      [configuredSessionConfig, retentionDays]
    );

    const open = props.open;
    const onOpenChange = props.onOpenChange;
    const onSend = props.onSend;
    const onReceive = props.onReceive;
    const onError = props.onError;
    const onThreadCreate = props.onThreadCreate;
    const onThreadUpdate = props.onThreadUpdate;
    const onThreadDelete = props.onThreadDelete;
    const onMessageCreate = props.onMessageCreate;
    const onHandoffRequest = props.onHandoffRequest;
    const onVoiceStart = props.onVoiceStart;
    const onVideoStart = props.onVideoStart;
    const onProductAction = props.onProductAction;
    const onAnalyticsEvent = props.onAnalyticsEvent;
    const buildMessagePayload = props.buildMessagePayload;
    const parseResponse = props.parseResponse;
    const buildThreadUrl = props.buildThreadUrl;
    const buildThreadRequest = props.buildThreadRequest;
    const buildThreadUpdateUrl = props.buildThreadUpdateUrl;
    const buildThreadDeleteUrl = props.buildThreadDeleteUrl;
    const parseThreadResponse = props.parseThreadResponse;
    const parseThreadCreateResponse = props.parseThreadCreateResponse;

    const [ui, uiDispatch] = useReducer(uiReducer, initialUIState);
    const widgetId = useId();
    const titleId = `${widgetId}-title`;
    const tabsIdBase = `${widgetId}-tabs`;
    const activeTabId = `${tabsIdBase}-tab-${ui.view}`;
    const activePanelId = `${tabsIdBase}-panel-${ui.view}`;
    const dialogId = `${widgetId}-dialog`;
    const mergedTranslations = useMemo(
      () => mergeMerchantStrings(merchantPreset, translations),
      [merchantPreset, translations]
    );
    const { strings: widgetStrings } = useWidgetStrings(language, mergedTranslations);
    const widgetDirection = useMemo(
      () => resolveTextDirection(language, textDirection),
      [language, textDirection]
    );
    const widgetLanguage = useMemo(() => String(language || 'en').trim() || 'en', [language]);

    const subject = subjectProp ?? '';

    // Consent, pre-chat and customer-email state (declared before analytics: the
    // analytics fields and the thread/chat requests carry these values).
    const customerEmailCapture = props.customerEmailCapture;
    const gating = useSendGatingState({
      consent,
      preChatForm,
      customerEmailCapture,
      readSetting,
      strings: widgetStrings,
    });
    const {
      preChatData,
      consentRequired,
      consentText,
      consentLinkText,
      safeConsentLinkUrl,
      consentVersion,
      hasConsent,
      customerEmail: {
        email: customerEmail,
        enabled: customerEmailCaptureEnabled,
        isValid: customerEmailValid,
        required: customerEmailRequired,
        setEmail: setCustomerEmail,
        setTouched: setCustomerEmailTouched,
        touched: customerEmailTouched,
        trimmedEmail: trimmedCustomerEmail,
      },
    } = gating;
    const [pendingSendMeta, setPendingSendMeta] = useState<Record<string, unknown> | null>(null);

    // Keyed on content, not identity: hosts often pass a fresh customFields object
    // every render, and trackAnalytics (and everything downstream of it) must not
    // rebuild unless the merged fields actually change.
    const analyticsFieldsKey = useMemo(() => {
      try {
        return JSON.stringify({ customFields, sessionVariables, preChatData });
      } catch {
        return `${Date.now()}-${Math.random()}`;
      }
    }, [customFields, sessionVariables, preChatData]);
    const analyticsFields = useMemo(
      () => ({
        ...customFields,
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [analyticsFieldsKey]
    );
    const analyticsContext = useMemo(
      () => ({
        orgId: agentOrgId || undefined,
        agentId: agentId || undefined,
        channel,
        provider: provider || undefined,
        companyName,
        customerId: customerId || undefined,
        source: 'response-chat-widget',
      }),
      [agentOrgId, agentId, channel, provider, companyName, customerId]
    );

    // ---- Hooks ----
    const trackAnalytics = useAnalytics({
      analyticsBaseUrl,
      apiKey,
      customFields: analyticsFields,
      analyticsContext,
      analyticsEnabled,
      analyticsSampleRate,
      analyticsEndpoint,
      analyticsApiKey: analyticsApiKey || undefined,
      sessionConfig,
      onAnalyticsEvent,
    });

    const { domainAllowed, domainCheckReady } = useDomainGate({
      allowedDomains,
      allowedOrigins,
      trackAnalytics,
    });

    // Top-level widget node (always mounted): host-page isolation and launcher lookup.
    const widgetRootRef = useRef<HTMLDivElement>(null);
    // The launcher remounts on every close, so focus is returned to the live one.
    const getLauncher = useCallback(
      () => widgetRootRef.current?.querySelector<HTMLElement>('.rw-minimized-button'),
      []
    );
    const { minimized, openChat, closeChat, closeChatForHandoff } = useOpenState({
      open,
      defaultOpen,
      onOpenChange,
      trackAnalytics,
      getFallbackFocus: getLauncher,
    });
    // Who asked for the panel to open. Only user-initiated opens (launcher click,
    // imperative open()) may move keyboard focus into the composer; auto-open must
    // not yank focus away from whatever the visitor was doing on the host page.
    const openSourceRef = useRef<'user' | 'auto'>('user');
    const openFromUser = useCallback(() => {
      openSourceRef.current = 'user';
      openChat();
    }, [openChat]);
    const openFromAuto = useCallback(() => {
      openSourceRef.current = 'auto';
      openChat();
    }, [openChat]);
    // Fullscreen on small viewports is a true modal (aria-modal, focus trap, inert and
    // scroll-locked host page); the floating desktop panel is a non-modal dialog.
    const isMobileFullscreen = isMobile && !minimized;
    useHostIsolation(isMobileFullscreen, widgetRootRef);
    const widgetStyle = useWidgetLayout({
      isMobile,
      position,
      fullscreen: ui.isFullscreen,
      zIndex,
    });
    useEffect(() => {
      uiDispatch({ type: 'SET_FULLSCREEN', isFullscreen: isMobileFullscreen });
    }, [isMobileFullscreen, uiDispatch]);

    const agentMeta = useMemo(() => {
      const meta = {
        orgId: agentOrgId || undefined,
        agentId: agentId || undefined,
        kbCollection: kbCollection || undefined,
        kbApiKey: kbApiKey || undefined,
        kbTopK: typeof kbTopK === 'number' ? kbTopK : undefined,
        kbScoreThreshold: typeof kbScoreThreshold === 'number' ? kbScoreThreshold : undefined,
      };
      const hasValues = Object.values(meta).some(value => value !== undefined && value !== '');
      return hasValues ? meta : null;
    }, [agentOrgId, agentId, kbCollection, kbApiKey, kbTopK, kbScoreThreshold]);

    const metaExtras = useMemo(
      () => ({
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
        ...(consentRequired ? { consent: { accepted: hasConsent, version: consentVersion } } : {}),
        ...(doNotTrain !== undefined || retentionDays !== undefined
          ? { policy: { doNotTrain, retentionDays } }
          : {}),
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(conversionPrompt ? { conversionPrompt } : {}),
        ...(agentMeta ? { agent: agentMeta } : {}),
      }),
      [
        preChatData,
        consentRequired,
        hasConsent,
        consentVersion,
        doNotTrain,
        retentionDays,
        sessionVariables,
        conversionPrompt,
        agentMeta,
      ]
    );
    const customerEmailMeta = useMemo(
      () =>
        customerEmailCaptureEnabled && customerEmailValid
          ? { email: trimmedCustomerEmail, customerEmail: trimmedCustomerEmail }
          : {},
      [customerEmailCaptureEnabled, customerEmailValid, trimmedCustomerEmail]
    );
    const effectiveCustomFields = useMemo(
      () => ({
        ...customFields,
        ...(customerEmailMeta.customerEmail
          ? { customerEmail: customerEmailMeta.customerEmail }
          : {}),
      }),
      [customFields, customerEmailMeta]
    );
    const effectiveMetaExtras = useMemo(
      () => ({
        ...metaExtras,
        ...customerEmailMeta,
      }),
      [metaExtras, customerEmailMeta]
    );

    const [threadPollIntervalMsEffective, setThreadPollIntervalMsEffective] =
      useState(threadPollIntervalMs);
    const isThreadControlled = !!threadIdControlled;

    const conversation = useConversationController();

    const storageScope = useMemo(() => ({ orgId: agentOrgId, agentId }), [agentOrgId, agentId]);
    const forgetBrowserConversation = useTranscriptPrivacy(
      storageScope,
      configuredSessionConfig,
      retentionDays,
      () => {
        cancelHistorySave();
        conversation.reset();
      }
    );
    const thread = useThread({
      threadIdProp,
      threadIdControlled: isThreadControlled,
      threadEndpoint,
      messageEndpoint,
      apiBaseUrl,
      apiKey,
      widgetAuthToken: widgetAuthToken || undefined,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
      provider,
      channel,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      threadPollIntervalMs: threadPollIntervalMsEffective,
      persistThread: effectivePersistThread && retentionDays !== 0,
      lazyLoad,
      minimized,
      subject,
      department,
      customFields: effectiveCustomFields,
      metaExtras: effectiveMetaExtras,
      sessionConfig,
      buildThreadUrl,
      buildThreadRequest,
      buildThreadUpdateUrl,
      buildThreadDeleteUrl,
      parseThreadResponse,
      parseThreadCreateResponse,
      onThreadCreate,
      onThreadUpdate,
      onThreadDelete,
      onConversationChange: conversation.replace,
      trackAnalytics,
      setMessages: conversation.setMessages,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const telemetryHeaders = useMemo(() => thread.withAuthHeaders(), [thread.withAuthHeaders]);
    const telemetry = useTelemetry({
      enabled: telemetryEnabled,
      endpoint: telemetryEndpoint,
      pollIntervalMs: telemetryPollIntervalMs,
      headers: telemetryHeaders,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
    });

    const outcomeTrackingConfig = useMemo<OutcomeTrackingConfig>(() => {
      const fromSettings = readSetting('outcomes');
      const base =
        fromSettings && typeof fromSettings === 'object' && !Array.isArray(fromSettings)
          ? (fromSettings as Record<string, unknown>)
          : {};
      const override =
        props.outcomes && typeof props.outcomes === 'object' && !Array.isArray(props.outcomes)
          ? (props.outcomes as Record<string, unknown>)
          : {};
      const merged: Record<string, unknown> = { ...base, ...override };

      const cfg: OutcomeTrackingConfig = {};
      if (typeof merged.enabled === 'boolean') cfg.enabled = merged.enabled;
      if (typeof merged.action === 'string') cfg.action = merged.action;
      if (typeof merged.provider === 'string') cfg.provider = merged.provider;
      if (typeof merged.customEventName === 'string') cfg.customEventName = merged.customEventName;
      if (typeof merged.postMessageType === 'string') cfg.postMessageType = merged.postMessageType;
      if (typeof merged.storageKey === 'string') cfg.storageKey = merged.storageKey;
      if (typeof merged.consumeStorageKey === 'boolean')
        cfg.consumeStorageKey = merged.consumeStorageKey;
      if (Array.isArray(merged.allowedMessageOrigins)) {
        cfg.allowedMessageOrigins = merged.allowedMessageOrigins
          .map(v => (typeof v === 'string' ? v : null))
          .filter(Boolean) as string[];
      }
      return cfg;
    }, [readSetting, props.outcomes]);

    const outcomeTracking = useOutcomeTracking({
      config: outcomeTrackingConfig,
      trackAnalytics,
      threadId: thread.threadId,
      onOutcome: props.onOutcome,
      sessionConfig,
    });
    const trackCommerceOutcome = outcomeTracking.trackOutcome;

    const resolvedSettingsConsoleUrlTemplate = useMemo(() => {
      const explicitTemplate = settingsConsoleUrlTemplate?.trim();
      if (explicitTemplate) return explicitTemplate;

      if (!customerId?.trim()) return '';
      if (!props.settingsEndpoint) return '';

      const endpointValue = String(props.settingsEndpoint).trim();
      if (!endpointValue) return '';

      const hasWindowOrigin = typeof window !== 'undefined' && window.location?.origin;
      try {
        const endpointUrl = hasWindowOrigin
          ? new URL(endpointValue, window.location.origin)
          : new URL(endpointValue);
        return `${endpointUrl.origin}${DEFAULT_SETTINGS_CONSOLE_URL_TEMPLATE}`;
      } catch {
        return '';
      }
    }, [customerId, props.settingsEndpoint, settingsConsoleUrlTemplate]);

    const settingsConsoleUrl = useMemo(() => {
      if (!resolvedSettingsConsoleUrlTemplate) return '';
      const hasPlaceholder = SETTINGS_CONSOLE_URL_TEMPLATES.test(
        resolvedSettingsConsoleUrlTemplate
      );
      const resolvedCustomerId = customerId?.trim();
      if (hasPlaceholder && !resolvedCustomerId) return '';

      if (!hasPlaceholder) return resolvedSettingsConsoleUrlTemplate.trim();

      const encodedCustomerId = encodeURIComponent(resolvedCustomerId || '');
      return resolvedSettingsConsoleUrlTemplate
        .replace(/\{customerId\}/g, encodedCustomerId)
        .replace(/\{id\}/g, encodedCustomerId)
        .replace(/:customerId/g, encodedCustomerId)
        .replace(/:id/g, encodedCustomerId);
    }, [customerId, resolvedSettingsConsoleUrlTemplate]);

    const safeSettingsConsoleUrl = useMemo(
      () => (settingsConsoleUrl && isSafeUrl(settingsConsoleUrl) ? settingsConsoleUrl : ''),
      [settingsConsoleUrl]
    );

    // Scope persisted history and thread ids per org/agent so two widgets on one origin do not
    // share state; the hooks fall back to the legacy global keys when the scope is incomplete.

    const chat = useChat({
      storageScope,
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      streamEndpoint,
      messageEndpoint,
      messageCreateEndpoint,
      apiBaseUrl,
      apiKey,
      provider,
      subject,
      department,
      channel,
      customFields: effectiveCustomFields,
      metaExtras: effectiveMetaExtras,
      threadId: thread.threadId,
      threadBase: thread.threadBase,
      companyName,
      saveHistory: retentionDays !== 0,
      loadHistory: !sessionHistoryEnabled,
      historyStorage: sessionConfig?.storage || 'local',
      buildMessagePayload,
      parseResponse,
      onSend,
      onReceive,
      onError,
      onMessageCreate,
      trackAnalytics,
      trackOutcome: outcomeTracking.trackOutcome,
      withAuthHeaders: thread.withAuthHeaders,
      fetchThread: thread.fetchThread,
      fetchThreadResult: thread.fetchThreadResult,
      createThread: thread.createThread,
      threadCreateEndpoint,
      minimized,
      setThreadId: thread.setThreadId,
      onThreadIssued: thread.onThreadIssued,
      sessionConfig,
      retryPolicy,
      strings: widgetStrings,
    });

    // conversation_end analytics, idle prompt/timeout, escalation polling + fallback.
    useConversationLifecycle({
      conversation,
      chat,
      thread,
      minimized,
      trackAnalytics,
      strings: widgetStrings,
      idleTimeoutConfig,
      threadPollIntervalMs,
      threadPollIntervalMsEffective,
      setThreadPollIntervalMsEffective,
    });

    // Pre-send gates (domain, pre-chat, consent, availability, limits, email, rate limit).
    const {
      isOffline,
      showPreChat,
      inputDisabled,
      statusMessage,
      passesSendGuards,
      ensureCustomerEmail,
      handleConsentToggle,
      handlePreChatSubmit,
    } = useSendGating({
      gating,
      domainAllowed,
      availability,
      offlineMessage,
      maxMessagesPerSession,
      rateLimit,
      messages: chat.state.messages,
      chatDispatch: chat.dispatch,
      trackAnalytics,
      strings: widgetStrings,
    });

    const sessionHistoryActive = sessionHistoryEnabled && retentionDays !== 0;
    const {
      sessionsOpen,
      setSessionsOpen,
      sessions,
      activeSessionId,
      createNewSession,
      selectSession,
      removeSession,
    } = useSessionHistory({
      storageScope,
      active: sessionHistoryActive,
      onBeforeChange: conversation.invalidate,
      maxSessions,
      setMessages: chat.setMessages,
      chatDispatch: chat.dispatch,
      setThreadId: thread.setThreadId,
      uiDispatch,
      messages: chat.state.messages,
      threadId: thread.threadId ?? null,
      sessionConfig,
    });

    conversation.bind({
      chat,
      thread,
      history: { active: sessionHistoryActive, createNewSession },
      uiDispatch,
    });

    const messagesForStorage = useMemo(
      () => sanitizeMessagesForStorage(chat.state.messages),
      [chat.state.messages]
    );

    // Debounced storage persistence (uses sessionStorage when configured)
    const historyStorageKind = sessionConfig?.storage || 'local';
    const cancelHistorySave = useLocalStorage(
      scopedStorageKey(STORAGE_KEY, storageScope),
      messagesForStorage,
      !sessionHistoryEnabled && retentionDays !== 0,
      300,
      historyStorageKind
    );

    const handleStartNewChat = useCallback(() => {
      conversation.startNew();
      trackAnalytics('new_chat_started', { source: 'quick_action' });
    }, [conversation, trackAnalytics]);

    const handleForgetConversation = useCallback(() => {
      cancelHistorySave();
      forgetBrowserConversation(activeSessionId, thread.threadId);
      setCustomerEmail('');
      if (activeSessionId) removeSession(activeSessionId);
      conversation.reset();
    }, [
      conversation,
      thread,
      activeSessionId,
      removeSession,
      forgetBrowserConversation,
      cancelHistorySave,
      setCustomerEmail,
    ]);

    const sessionsPanelRef = useRef<HTMLDivElement>(null);
    const sessionsPreviousFocusRef = useRef<HTMLElement | null>(null);
    const sessionsFocusRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openSessions = useCallback(() => {
      try {
        sessionsPreviousFocusRef.current = document.activeElement as HTMLElement | null;
      } catch {
        sessionsPreviousFocusRef.current = null;
      }
      setSessionsOpen(true);
    }, [setSessionsOpen]);

    const closeSessions = useCallback(() => {
      setSessionsOpen(false);
      // Restore focus to whatever opened the sessions panel, if possible.
      if (sessionsFocusRestoreTimeoutRef.current) {
        clearTimeout(sessionsFocusRestoreTimeoutRef.current);
      }
      sessionsFocusRestoreTimeoutRef.current = setTimeout(() => {
        sessionsPreviousFocusRef.current?.focus();
      }, 0);
    }, [setSessionsOpen]);

    const {
      messagesEndRef,
      messagesContainerRef,
      scrollToBottomVisible,
      newMessagesWhileScrolledUp,
      handleMessagesScroll,
      scrollToBottom,
    } = useScrollManagement({
      messages: chat.state.messages,
      streamingMessage: chat.state.streamingMessage,
      activeSessionId,
      minimized,
    });

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const attachmentSequence = useRef(0);
    const attachmentsRef = useRef(ui.attachments);

    useEffect(() => {
      attachmentsRef.current = ui.attachments;
    }, [ui.attachments]);

    // When bubble options exist, skip the intro message so clicking a
    // bubble transitions directly from the empty state into the flow
    // without the welcome greeting lingering in the conversation.
    useIntroMessage({
      historyLoaded: chat.historyLoaded,
      messagesLength: chat.state.messages.length,
      welcomeMessage: filteredBubbleOptions.length > 0 ? '' : welcomeMessage || '',
      filteredBubbleOptions,
      activeSessionId,
      threadId: thread.threadId,
      dispatch: chat.dispatch,
    });

    useUnreadMessages(minimized, chat.state.messages, uiDispatch);

    // Focus composer when the user opens the chat (never on auto-open).
    useEffect(() => {
      if (!minimized) {
        const userInitiated = openSourceRef.current !== 'auto';
        // Reset so a later open defaults to user-initiated unless auto-open says otherwise.
        openSourceRef.current = 'user';
        if (!userInitiated) return;
        if (inputFocusTimeoutRef.current) {
          clearTimeout(inputFocusTimeoutRef.current);
        }
        const focusAtOpen = document.activeElement;
        const timer = setTimeout(() => {
          // Opening focus is a fallback: preserve any control the visitor has already chosen.
          if (document.activeElement !== focusAtOpen) return;
          try {
            textAreaRef.current?.focus();
          } catch {
            /* */
          }
        }, 50);
        inputFocusTimeoutRef.current = timer;
        return () => clearTimeout(timer);
      }
    }, [minimized]);

    const { panelWarmup, finishPanelWarmup, warmupRefs } = usePanelWarmup(minimized);

    useEffect(() => {
      return () => {
        if (sessionsFocusRestoreTimeoutRef.current) {
          clearTimeout(sessionsFocusRestoreTimeoutRef.current);
        }
        if (inputFocusTimeoutRef.current) {
          clearTimeout(inputFocusTimeoutRef.current);
        }
      };
    }, []);

    const widgetLoadedTrackedRef = useRef(false);
    const widgetLoadedDeferredRef = useRef(false);

    // Track widget load. Markdown and syntax highlighting load on demand.
    // Consent managers can mount with analytics off and enable it later
    // (api.setAnalyticsEnabled), so wait for analytics instead of dropping the
    // event; consentDelayed distinguishes late-consent loads in the data.
    useEffect(() => {
      if (widgetLoadedTrackedRef.current) return;
      if (analyticsEnabled === false) {
        widgetLoadedDeferredRef.current = true;
        return;
      }
      widgetLoadedTrackedRef.current = true;
      trackAnalytics('widget_loaded', { consentDelayed: widgetLoadedDeferredRef.current });
    }, [trackAnalytics, analyticsEnabled]);

    const autoOpenConfig = useMemo(() => {
      const hasAnyRule =
        typeof autoOpenDelayMs === 'number' ||
        typeof autoOpenRules?.delayMs === 'number' ||
        (Array.isArray(autoOpenRules?.pageRules) && autoOpenRules.pageRules.length > 0) ||
        typeof autoOpenRules?.scrollPercent === 'number' ||
        !!autoOpenRules?.exitIntent ||
        typeof autoOpenRules?.idleMs === 'number';
      return {
        enabled: typeof autoOpenRules?.enabled === 'boolean' ? autoOpenRules.enabled : hasAnyRule,
        delayMs:
          typeof autoOpenRules?.delayMs === 'number' ? autoOpenRules.delayMs : autoOpenDelayMs,
        pageRules: autoOpenRules?.pageRules,
        scrollPercent: autoOpenRules?.scrollPercent,
        exitIntent: autoOpenRules?.exitIntent,
        idleMs: autoOpenRules?.idleMs,
        maxOpensPerSession: autoOpenRules?.maxOpensPerSession ?? 1,
        disableOnMobile: autoOpenRules?.disableOnMobile,
      };
    }, [autoOpenRules, autoOpenDelayMs]);

    // Auto-open rules
    useAutoOpen({
      controlledOpen: open,
      config: autoOpenConfig,
      minimized,
      domainAllowed: domainCheckReady && domainAllowed,
      openChat: openFromAuto,
      trackAnalytics,
      merchantPreset,
    });

    // Keyboard navigation & focus trap
    useKeyboardNavigation({
      minimized,
      sessionsOpen,
      closeSessions,
      closeChat,
      chatContainerRef,
      sessionsPanelRef,
      modal: isMobileFullscreen,
    });

    // Revoke object URLs on unmount
    useEffect(() => {
      return () => {
        attachmentsRef.current.forEach(a => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        });
      };
    }, []);

    // ---- Derived data ----
    // No sample data in production: a merchant that passes no records gets empty views,
    // never fabricated orders. Demo pages pass their own `records` explicitly.
    const mergedRecords = useMemo(
      () => ({
        orders: records?.orders ?? EMPTY_RECORDS.orders,
        products: records?.products ?? EMPTY_RECORDS.products,
        subscriptions: records?.subscriptions ?? EMPTY_RECORDS.subscriptions,
        returns: records?.returns ?? EMPTY_RECORDS.returns,
        exchanges: records?.exchanges ?? EMPTY_RECORDS.exchanges,
      }),
      [records]
    );

    const viewCounts = useMemo(
      () => ({
        chat: chat.state.messages.length,
        orders: mergedRecords.orders?.length || 0,
        products: mergedRecords.products?.length || 0,
        subscriptions: mergedRecords.subscriptions?.length || 0,
        returns: mergedRecords.returns?.length || 0,
        exchanges: mergedRecords.exchanges?.length || 0,
      }),
      [chat.state.messages, mergedRecords]
    );

    const visibleViews = useMemo(
      () => getVisibleViews(viewCounts, ui.view, recordNavigation),
      [viewCounts, ui.view, recordNavigation]
    );
    const showViewTabs = configuredShowViewTabs && visibleViews.length > 1;

    const currentViewMode = useMemo(() => {
      const config = RECORD_VIEW_CONFIG[ui.view];
      if (!config) return undefined;
      return ui.viewModes[ui.view] ?? config.defaultMode ?? config?.modes?.[0]?.key ?? 'overview';
    }, [ui.view, ui.viewModes]);

    const fileValidationOptions = useMemo(
      () => ({
        allowedFileTypes,
        maxFileSize: typeof maxFileSizeMB === 'number' ? maxFileSizeMB * 1024 * 1024 : undefined,
        maxFileCount,
        strings: widgetStrings,
      }),
      [allowedFileTypes, maxFileSizeMB, maxFileCount, widgetStrings]
    );

    // ---- Handlers ----
    const handleViewModeChange = useCallback(
      (viewKey: string, modeKey: string) => {
        uiDispatch({ type: 'SET_VIEW_MODE', viewKey, modeKey });
        trackAnalytics('view_mode_change', { view: viewKey, mode: modeKey });
      },
      [trackAnalytics]
    );

    const handleExport = useCallback(
      (format: string) => {
        void import(/* webpackChunkName: "export" */ './utils/export').then(
          ({ exportConversation }) =>
            exportConversation(chat.state.messages, format, widgetStrings.locale)
        );
        trackAnalytics('conversation_exported', {
          format,
          messageCount: chat.state.messages.length,
        });
      },
      [widgetStrings.locale, chat.state.messages, trackAnalytics]
    );

    const focusComposerSoon = useCallback(() => {
      if (inputFocusTimeoutRef.current) {
        clearTimeout(inputFocusTimeoutRef.current);
      }
      const focusAtRequest = document.activeElement;
      inputFocusTimeoutRef.current = setTimeout(() => {
        const active = document.activeElement;
        if (active === focusAtRequest || active === document.body) {
          const target =
            textAreaRef.current ||
            chatContainerRef.current
              ?.querySelector('.rw-prechat-form')
              ?.querySelector<HTMLElement>('input, select, textarea, button');
          target?.focus();
        }
      }, 0);
    }, []);

    const handleHome = useCallback(() => {
      conversation.startNew();
      trackAnalytics('chat_home', {
        sessionHistoryActive,
        messageCount: chat.state.messages.length,
      });
      focusComposerSoon();
    }, [sessionHistoryActive, conversation, chat, trackAnalytics, focusComposerSoon]);

    const handleFileUpload = useCallback(
      (fileList: FileList) => {
        if (!allowAttachments || inputDisabled) return;
        const files = Array.from(fileList || []);
        const { valid, errors } = validateFiles(
          files,
          ui.attachments.length,
          fileValidationOptions
        );
        if (errors.length) {
          console.warn(
            'File validation errors:',
            errors.map(e => e.reason)
          );
          uiDispatch({ type: 'SET_ATTACHMENT_ERRORS', errors: errors.map(e => e.reason) });
          trackAnalytics('file_validation_error', {
            count: errors.length,
            reasons: errors.map(e => e.reason),
            fileNames: errors.map(e => e.file?.name).filter(Boolean),
            fileTypes: errors.map(e => e.file?.type).filter(Boolean),
            totalRejectedBytes: errors.reduce((sum, e) => sum + (e.file?.size || 0), 0),
          });
        } else {
          uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
        }
        const mapped: Attachment[] = valid.map(file => ({
          id: `${widgetId}-${++attachmentSequence.current}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        if (mapped.length > 0) {
          trackAnalytics('file_upload', {
            status: 'selected',
            count: mapped.length,
            fileNames: mapped.map(file => file.name),
            fileTypes: mapped.map(file => file.type),
            totalBytes: mapped.reduce((sum, file) => sum + file.size, 0),
          });
        }
        uiDispatch({ type: 'ADD_ATTACHMENTS', attachments: mapped });
      },
      [
        allowAttachments,
        inputDisabled,
        ui.attachments.length,
        trackAnalytics,
        fileValidationOptions,
        widgetId,
      ]
    );

    const handleSend = useCallback(async () => {
      if (!passesSendGuards()) return;

      const atts = ui.attachments;
      const hasAttachments = atts.length > 0;
      const sendMeta = hasAttachments ? pendingSendMeta || undefined : undefined;
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      const sendResult = await chat.handleSend(atts, sendMeta);
      if (sendResult.ok && !sendResult.aborted) {
        uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
        if (hasAttachments) setPendingSendMeta(null);
      } else if (!hasAttachments && pendingSendMeta) {
        setPendingSendMeta(null);
      }
    }, [passesSendGuards, chat, ui.attachments, pendingSendMeta]);

    // Retry from a failed bubble: same policy checks, then replay the message.
    const handleRetryMessage = useCallback(
      async (messageId: string) => {
        if (!passesSendGuards(messageId)) return;
        return chat.retryMessage(messageId);
      },
      [passesSendGuards, chat]
    );

    // Offline: banner in the panel; sends queued while offline go out on reconnect.
    const isOnline = useOnlineStatus();
    useRetryOnReconnect(
      isOnline,
      chat.state.messages,
      handleRetryMessage,
      chat.state.loading || !!chat.state.streamingMessage
    );

    // Thumbs up/down on assistant messages: optimistic reducer update + debounced POST.
    // `feedbackEndpoint` is resolved from `apiBaseUrl` (`<apiBaseUrl>/response/feedback`)
    // until the config alias table grows a dedicated `endpoints.feedbackEndpoint` entry.
    const { setReaction: handleMessageReaction } = useMessageFeedback({
      threadId: thread.threadId,
      apiBaseUrl,
      feedbackEndpoint,
      messages: chat.state.messages,
      withAuthHeaders: thread.withAuthHeaders,
      sessionConfig,
      resolveServerMessageId: thread.resolveServerMessageId,
      dispatch: chat.dispatch,
      trackAnalytics,
    });

    // Latest failed-send reason, announced by the sr-only alert when the message log
    // (and its inline bubble caption) is not on screen.
    const lastFailedSendError = useMemo(() => {
      const messages = chat.state.messages;
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (message.status === 'failed') return message.error || widgetStrings.sendFailed;
      }
      return '';
    }, [chat.state.messages, widgetStrings.sendFailed]);

    const { handleButtonClick, handleFormSubmit } = useBusinessActions({
      chatDispatch: chat.dispatch,
      chatLoading: chat.state.loading,
      createMessage: chat.createMessage,
      threadId: thread.threadId ?? null,
      createThread: thread.createThread,
      threadCreateEndpoint,
      subject,
      companyName,
      channel,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      onHandoffRequest,
      onSelfieUploadIntent: () => setPendingSendMeta({ intent: 'shade_selfie_upload' }),
      onStartNewChat: handleStartNewChat,
      preChatData,
      trackAnalytics,
      onError,
      strings: widgetStrings,
      merchantPreset,
      merchantPresetLoaded: resolved.merchantPresetLoaded,
    });

    const { localMessageFlows, addLocalMessage } = useLocalMessages(
      props.localMessageFlows,
      chat.dispatch
    );

    const handleBeforeCartOpen = useCallback(() => {
      if (!isMobileFullscreen || !widgetRootRef.current?.querySelector('[role="dialog"]')) return;
      // Release inert, the body lock and the focus trap before the theme opens its modal.
      // Otherwise the drawer can inherit the chat lock and leave checkout untappable.
      flushSync(() => closeChatForHandoff());
    }, [isMobileFullscreen, closeChatForHandoff]);

    const { handleProductAction, handleCartConfirmationAction } = useCommerceActions({
      chatDispatch: chat.dispatch,
      messages: chat.state.messages,
      trackAnalytics,
      trackCommerceOutcome,
      scrollToBottom,
      closeChat,
      merchantPreset,
      strings: widgetStrings,
      cartMode,
      cartEndpoint,
      cartAttributionEndpoint,
      apiBaseUrl,
      messageEndpoint,
      apiKey,
      widgetAuthToken,
      agentId,
      agentOrgId,
      threadId: thread.threadId,
      withAuthHeaders: thread.withAuthHeaders,
      sessionConfig,
      onProductAction,
      onBeforeCartOpen: handleBeforeCartOpen,
      addLocalMessage,
      handleButtonClick,
    });

    const handleWidgetAction = useCallback(
      async (value: string) => {
        if (!value) return;
        if (!ensureCustomerEmail()) return;

        if (await handleCartConfirmationAction(value)) return;

        const flow = localMessageFlows[value];
        if (!flow) {
          await handleButtonClick(value);
          return;
        }

        trackAnalytics('widget_action_click', {
          action: 'local_flow',
          value,
        });

        if (flow.userMessage) {
          addLocalMessage('user', { text: flow.userMessage }, 'sent');
        }
        addLocalMessage('ai', flow.reply, 'delivered');
      },
      [
        addLocalMessage,
        handleButtonClick,
        handleCartConfirmationAction,
        localMessageFlows,
        trackAnalytics,
        ensureCustomerEmail,
      ]
    );

    const customerEmailCaptureNode = useMemo(() => {
      if (!customerEmailCaptureEnabled || !customerEmailCapture) return null;
      return (
        <React.Suspense fallback={null}>
          <CustomerEmailCapture
            config={customerEmailCapture}
            email={customerEmail}
            inputId={`${widgetId}-customer-email`}
            isValid={customerEmailValid}
            onChange={setCustomerEmail}
            onTouched={() => setCustomerEmailTouched(true)}
            required={customerEmailRequired}
            touched={customerEmailTouched}
            strings={widgetStrings}
          />
        </React.Suspense>
      );
    }, [
      customerEmail,
      customerEmailCapture,
      customerEmailCaptureEnabled,
      customerEmailRequired,
      customerEmailTouched,
      customerEmailValid,
      setCustomerEmail,
      setCustomerEmailTouched,
      widgetId,
      widgetStrings,
    ]);

    const handleOrderAction = useCallback(
      ({
        action,
        orderId,
      }: {
        action: {
          label: string;
          value?: string;
          href?: string;
          target?: '_self' | '_blank';
        };
        orderId?: string | null;
      }) => {
        const isReorderAction = isReorderLikeAction(action.label, action.value || '');
        const actionUrl = action.href?.trim();
        const hasActionValue = Boolean((action.value || '').trim());
        const safeUrl = actionUrl && isSafeUrl(actionUrl) ? actionUrl : '';
        const actionRoute = isReorderAction ? 'pdp' : 'default';
        trackAnalytics('order_action', {
          label: action.label,
          value: action.value || null,
          orderId: orderId || null,
          route: actionRoute,
          hasHref: Boolean(safeUrl),
        });
        if (safeUrl) {
          const target = action.target || '_blank';
          if (target === '_self') {
            try {
              window.open(safeUrl, '_self');
            } catch {
              try {
                window.location.assign(safeUrl);
              } catch {
                // Ignore if navigation is blocked.
              }
            }
          } else {
            window.open(safeUrl, target, 'noopener,noreferrer');
          }
          return;
        }
        if (hasActionValue) {
          handleWidgetAction(action.value || '');
        }
      },
      [handleWidgetAction, trackAnalytics]
    );

    const handleVoiceClick = useCallback(() => {
      trackAnalytics('voice_click', {});
      onVoiceStart?.();
    }, [trackAnalytics, onVoiceStart]);

    const handleVideoClick = useCallback(() => {
      trackAnalytics('video_click', {});
      onVideoStart?.();
    }, [trackAnalytics, onVideoStart]);

    // Drag and drop
    const { handleDragEnter, handleDragLeave, handleDragOver, handleDrop, isDraggingFiles } =
      useDragDrop({
        containerRef: chatContainerRef,
        onDrop: handleFileUpload,
        enabled: allowAttachments && !inputDisabled && !minimized,
      });

    // ---- Imperative handle ----
    useImperativeApi(ref, {
      openFromUser,
      closeChat,
      minimized,
      thread,
      createMessage: chat.createMessage,
      sessionConfig,
      trackOutcome: outcomeTracking.trackOutcome,
    });

    // Automatic cart directives are transport messages. The transactional success or failure
    // message becomes visible after Shopify answers, so customers never see a premature success.
    // No count cap here: MainContent windows the log (settings.behavior.maxRenderedMessages), so
    // the whole restored history stays reachable through "Show earlier messages".
    const displayMessages = useMemo(
      () => chat.state.messages.filter(message => !message.richContent?.autoAction),
      [chat.state.messages]
    );

    // Static config for deep children (MessageBubble, OrderCardsMessage); message state
    // reaches MainContent via props, so no per-render state context is needed.
    const chatConfigValue: ChatConfigValue = useMemo(
      () => ({
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      }),
      [
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      ]
    );

    // Stable handlers for memoized children (Header, MainContent, InputArea).
    const handleViewChange = useCallback(
      (v: string) => {
        uiDispatch({ type: 'SET_VIEW', view: v });
        if (configuredShowViewTabs && getVisibleViews(viewCounts, v, recordNavigation).length === 1)
          focusComposerSoon();
      },
      [configuredShowViewTabs, viewCounts, recordNavigation, focusComposerSoon]
    );
    const handleScrollToBottomClick = useCallback(() => scrollToBottom('smooth'), [scrollToBottom]);
    const setInputValue = useCallback(
      (v: string) => chat.dispatch({ type: 'SET_INPUT', input: v }),
      [chat]
    );
    const handleRemoveAttachment = useCallback(
      (id: string) => {
        const attachment = attachmentsRef.current.find(item => item.id === id);
        trackAnalytics('file_upload', {
          status: 'removed',
          fileName: attachment?.name,
          fileType: attachment?.type,
          fileSize: attachment?.size,
        });
        uiDispatch({ type: 'REMOVE_ATTACHMENT', id });
      },
      [trackAnalytics, uiDispatch]
    );

    // ---- Render ----
    return (
      <MerchantPresetProvider value={merchantPreset}>
        <ChatConfigProvider value={chatConfigValue}>
          <div
            ref={widgetRootRef}
            className="rw-widget"
            style={widgetStyle}
            data-rw-theme={darkMode}
            data-rw-mobile={isMobileFullscreen ? 'true' : 'false'}
            data-rw-dir={widgetDirection}
            lang={widgetLanguage}
            dir={widgetDirection}
          >
            {customCss ? (
              <style
                nonce={cspNonce || undefined}
                dangerouslySetInnerHTML={{ __html: customCss }}
              />
            ) : null}
            {minimized ? (
              <MinimizedButton
                unreadCount={ui.unreadCount}
                theme={theme}
                onClick={openFromUser}
                controlsId={dialogId}
                strings={widgetStrings}
              />
            ) : (
              <div
                className={`flex flex-col rw-panel ${
                  ui.isFullscreen ? 'w-full h-full' : 'w-96 h-[600px]'
                } bg-white rounded-2xl animate-slideUp overflow-x-hidden relative`}
                ref={chatContainerRef}
                id={dialogId}
                role="dialog"
                aria-modal={isMobileFullscreen ? 'true' : undefined}
                aria-label={formatWidgetString(widgetStrings.dialogAriaLabel, {
                  company: companyName,
                })}
                aria-labelledby={titleId}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {allowAttachments && isDraggingFiles && (
                  <div className="rw-drag-overlay" role="status" aria-live="polite">
                    <div className="rw-drag-overlay-inner">{widgetStrings.dropFilesToUpload}</div>
                  </div>
                )}
                <Header
                  view={ui.view}
                  viewCounts={viewCounts}
                  theme={theme}
                  companyName={companyName}
                  companyLogo={companyLogo}
                  agentName={agentName}
                  agentAvatar={agentAvatar}
                  titleId={titleId}
                  tabsIdBase={tabsIdBase}
                  showViewTabs={showViewTabs}
                  visibleViews={visibleViews}
                  offline={!isOnline || isOffline}
                  showSessionsButton={sessionHistoryActive}
                  onSessionsClick={openSessions}
                  onViewChange={handleViewChange}
                  onMinimize={closeChat}
                  onHome={handleHome}
                  onExport={handleExport}
                  onForget={handleForgetConversation}
                  settingsConsoleUrl={safeSettingsConsoleUrl}
                  settingsConsoleLabel={settingsConsoleLabel}
                  strings={widgetStrings}
                />

                {!isOnline && (
                  <div className="rw-offline-banner" role="status" aria-live="polite">
                    {widgetStrings.connectionOffline}
                  </div>
                )}

                {sessionHistoryActive && sessionsOpen && (
                  <React.Suspense fallback={null}>
                    <SessionPanel
                      sessions={sessions}
                      activeSessionId={activeSessionId}
                      containerRef={sessionsPanelRef}
                      onClose={closeSessions}
                      onCreate={() => {
                        conversation.startNew();
                        focusComposerSoon();
                      }}
                      onSelect={id => {
                        selectSession(id);
                        focusComposerSoon();
                      }}
                      onDelete={id => {
                        removeSession(id);
                        if (id === activeSessionId) focusComposerSoon();
                      }}
                      strings={widgetStrings}
                    />
                  </React.Suspense>
                )}

                {showPreChat ? (
                  // The view tabs reference this panel id via aria-controls, so it must exist
                  // while the pre-chat form stands in for the conversation panel.
                  <div
                    id={activePanelId}
                    role="tabpanel"
                    aria-labelledby={showViewTabs ? activeTabId : titleId}
                    className="flex-1 min-h-0 flex flex-col"
                  >
                    <React.Suspense fallback={null}>
                      <PreChatForm
                        config={preChatForm}
                        onSubmit={data => {
                          handlePreChatSubmit(data);
                          focusComposerSoon();
                        }}
                        strings={widgetStrings}
                      />
                    </React.Suspense>
                  </div>
                ) : (
                  <>
                    <MainContent
                      view={ui.view}
                      viewMode={currentViewMode}
                      onModeChange={handleViewModeChange}
                      records={mergedRecords}
                      messages={displayMessages}
                      isTyping={chat.state.isTyping}
                      isMobile={isMobileFullscreen}
                      error={chat.state.error}
                      messagesEndRef={messagesEndRef}
                      messagesContainerRef={messagesContainerRef}
                      onMessagesScroll={handleMessagesScroll}
                      streamingMessage={chat.state.streamingMessage}
                      activeToolCalls={chat.state.activeToolCalls}
                      onButtonClick={handleWidgetAction}
                      panelId={activePanelId}
                      labelledBy={showViewTabs ? activeTabId : titleId}
                      emptyStateMessage={welcomeMessage || undefined}
                      bubbleOptions={filteredBubbleOptions}
                      customerEmailCapture={customerEmailCaptureNode}
                      suggestedActions={suggestedActions}
                      localizeActions={localizeActions}
                      onSuggestedAction={handleWidgetAction}
                      onProductAction={handleProductAction}
                      onOrderAction={handleOrderAction}
                      onFormSubmit={handleFormSubmit}
                      onRetryMessage={handleRetryMessage}
                      retryDisabled={chat.state.loading || !!chat.state.streamingMessage}
                      onReaction={handleMessageReaction}
                      showScrollToBottom={scrollToBottomVisible && ui.view === 'chat'}
                      newMessagesCount={newMessagesWhileScrolledUp}
                      onScrollToBottom={handleScrollToBottomClick}
                      strings={widgetStrings}
                      direction={widgetDirection}
                      maxRenderedMessages={maxRenderedMessages}
                      atBottom={!scrollToBottomVisible}
                    />

                    {telemetryEnabled && ui.view === 'chat' && (
                      <React.Suspense fallback={null}>
                        <TelemetryPanel
                          data={telemetry.data}
                          loading={telemetry.loading}
                          error={telemetry.error}
                          showContext={telemetryShowContext}
                          onRefresh={telemetry.refresh}
                          strings={widgetStrings}
                        />
                      </React.Suspense>
                    )}

                    {isOffline && offlineMessage && (
                      <div className="px-4 py-2 text-xs text-amber-500 bg-gray-50 border-t border-gray-200">
                        {offlineMessage}
                      </div>
                    )}

                    {consentRequired && (
                      <div className="px-4 py-2 text-xs text-gray-600 flex items-start gap-2 border-t border-gray-200">
                        <input
                          type="checkbox"
                          checked={hasConsent}
                          onChange={e => handleConsentToggle(e.target.checked)}
                          aria-label={widgetStrings.consentCheckboxLabel}
                          disabled={!domainAllowed}
                        />
                        <span>
                          {consentText}{' '}
                          {safeConsentLinkUrl && (
                            <a
                              href={safeConsentLinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              {consentLinkText}
                            </a>
                          )}
                        </span>
                      </div>
                    )}

                    {statusMessage && (
                      <div className="px-4 pb-2 text-xs text-gray-500">{statusMessage}</div>
                    )}

                    <InputArea
                      input={chat.state.input}
                      setInput={setInputValue}
                      handleSend={handleSend}
                      loading={chat.state.loading || !!chat.state.streamingMessage}
                      onStop={chat.abortActiveStream}
                      attachments={ui.attachments}
                      attachmentErrors={ui.attachmentErrors}
                      allowAttachments={allowAttachments}
                      allowVoice={allowVoice}
                      allowVideo={allowVideo}
                      theme={theme}
                      fileInputRef={fileInputRef}
                      textAreaRef={textAreaRef}
                      onFilesSelected={handleFileUpload}
                      onRemoveAttachment={handleRemoveAttachment}
                      onVoiceClick={handleVoiceClick}
                      onVideoClick={handleVideoClick}
                      disabled={inputDisabled}
                      maxInputLength={maxInputLength}
                      inputWarningThreshold={inputWarningThreshold}
                      allowedFileTypes={allowedFileTypes}
                      strings={widgetStrings}
                      placeholder={placeholder || widgetStrings.messagePlaceholder}
                    />
                  </>
                )}
              </div>
            )}

            {minimized && panelWarmup === 'active' && (
              <PanelWarmup
                onDone={finishPanelWarmup}
                frameClassName="flex flex-col rw-panel w-96 h-[600px] bg-white rounded-2xl overflow-x-hidden relative"
              >
                <Header
                  view="chat"
                  viewCounts={viewCounts}
                  theme={theme}
                  companyName={companyName}
                  companyLogo={companyLogo}
                  agentName={agentName}
                  agentAvatar={agentAvatar}
                  titleId={`${titleId}-warmup`}
                  tabsIdBase={`${tabsIdBase}-warmup`}
                  showViewTabs={showViewTabs}
                  visibleViews={visibleViews}
                  offline={!isOnline || isOffline}
                  showSessionsButton={sessionHistoryActive}
                  onSessionsClick={openSessions}
                  onViewChange={handleViewChange}
                  onMinimize={closeChat}
                  onHome={handleHome}
                  onExport={handleExport}
                  onForget={handleForgetConversation}
                  settingsConsoleUrl={safeSettingsConsoleUrl}
                  settingsConsoleLabel={settingsConsoleLabel}
                  strings={widgetStrings}
                  showExportMenu={false}
                />
                <MainContent
                  view="chat"
                  onModeChange={handleViewModeChange}
                  records={EMPTY_RECORDS}
                  messages={EMPTY_MESSAGES}
                  isTyping={false}
                  error=""
                  messagesEndRef={warmupRefs.end}
                  streamingMessage={null}
                  activeToolCalls={EMPTY_TOOL_CALLS}
                  panelId={`${activePanelId}-warmup`}
                  labelledBy={`${titleId}-warmup`}
                  emptyStateMessage={welcomeMessage || undefined}
                  bubbleOptions={filteredBubbleOptions}
                  suggestedActions={suggestedActions}
                  localizeActions={localizeActions}
                  onSuggestedAction={handleWidgetAction}
                  strings={widgetStrings}
                  direction={widgetDirection}
                  announceStreaming={false}
                  preloadRich={false}
                />
                <InputArea
                  input=""
                  setInput={setInputValue}
                  handleSend={handleSend}
                  loading={false}
                  attachments={EMPTY_ATTACHMENTS}
                  attachmentErrors={EMPTY_STRINGS}
                  allowAttachments={allowAttachments}
                  allowVoice={allowVoice}
                  allowVideo={allowVideo}
                  theme={theme}
                  fileInputRef={warmupRefs.fileInput}
                  textAreaRef={warmupRefs.textArea}
                  onFilesSelected={handleFileUpload}
                  onRemoveAttachment={handleRemoveAttachment}
                  maxInputLength={maxInputLength}
                  strings={widgetStrings}
                  placeholder={placeholder || widgetStrings.messagePlaceholder}
                />
              </PanelWarmup>
            )}

            {/* Accessibility live regions. Typing is already announced by the message
              log (role="log" aria-live="polite" + TypingIndicator), so it is not
              repeated here. */}
            <div className="sr-only" aria-live="polite">
              {chat.state.loading && widgetStrings.loading}
              {minimized &&
                ui.unreadCount > 0 &&
                formatCount(widgetStrings, 'liveRegionUnread', ui.unreadCount)}
            </div>
            {/* MainContent renders the error inline with role="alert" in the chat view; this
              region only speaks when that inline alert is not on screen. */}
            {(chat.state.error || lastFailedSendError) && (showPreChat || ui.view !== 'chat') && (
              <div className="sr-only" role="alert">
                {formatWidgetString(widgetStrings.errorAnnouncement, {
                  message: chat.state.error || lastFailedSendError,
                })}
              </div>
            )}
          </div>
        </ChatConfigProvider>
      </MerchantPresetProvider>
    );
  }
);

const ChatWidget = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidget(props, ref) {
    // The boundary sits outside the inner widget, so resolve its copy from props directly.
    const fallbackStrings = useMemo(
      () => resolveWidgetStrings(props.language, props.translations),
      [props.language, props.translations]
    );
    return (
      <ErrorBoundary strings={fallbackStrings}>
        <ChatWidgetInner {...props} ref={ref} />
      </ErrorBoundary>
    );
  }
);

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
